import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Footer, Navbar } from "./Home";

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return isMobile;
}


/* ─── API ─── */
const API_BASE =
  "https://mediumaquamarine-reindeer-844418.hostingersite.com/manage/api.php";

/* ─── Image map ─── */
const IMG = {
  warehouse: "/assets/images/new/2.jpg",
  whoWeAre: "/assets/images/new/8.jpg",
  visionMission: "/assets/images/new/3.jpg",
  freshProduce: "/assets/images/new/4.jpg",
  supplyChain: "/assets/images/new/5.jpg",
  globalNetwork: "/assets/images/new/6.jpg",
  customers: "/assets/images/new/7.jpg",
  strength: "/assets/images/new/8.jpg",
  contact: "/assets/images/new/9.jpg",
  pineappleDole: "/assets/images/new/10.jpg",
  bananaDole: "/assets/images/new/11.jpg",
  pineappleOla: "/assets/images/new/12.jpg",
  pineappleOla2: "/assets/images/new/13.jpg",
  bananaTerioka: "/assets/images/new/15.png",
  whyPartner: "/assets/images/new/13.jpg",
};

/* ─── Utilities ─── */
function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

function useInView(ref, threshold = 0.12) {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold },
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return vis;
}

/* ─── Global CSS ─── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow-x:hidden;background:#fff}
  @keyframes fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:translateY(0)}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
  @keyframes fadeSlideUp{0%{opacity:0;transform:translateY(40px)}100%{opacity:1;transform:translateY(0)}}
  @keyframes popIn{from{opacity:0;transform:scale(0.7)}to{opacity:1;transform:scale(1)}}
  @keyframes cardSlideIn{from{opacity:0;transform:translateY(60px) scale(0.94)}to{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes pulseRing{0%{transform:scale(1);opacity:0.6}100%{transform:scale(1.3);opacity:0}}
  @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes gradientFlow{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes shimmerSkeleton{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
  .ani-fadeup{animation:fadeUp 0.85s cubic-bezier(.22,1,.36,1) both}
  .serif{font-family:'Georgia',Georgia,serif}
  .shine-text{
    background:linear-gradient(90deg,#dd3c30 0%,#fff 40%,#dd3c30 60%,#fff 100%);
    background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;animation:shimmer 4s linear infinite
  }
  .sl{font-size:11px;letter-spacing:3px;color:#dd3c30;font-weight:700;text-transform:uppercase;margin-bottom:14px}
  .sl-dark{font-size:11px;letter-spacing:3px;color:#276530;font-weight:700;text-transform:uppercase;margin-bottom:14px}
  .btn-green{display:inline-block;background:#276530;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-green:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(39,101,48,0.45)}
  .btn-ghost{display:inline-block;background:rgba(255,255,255,0.12);color:#fff;padding:13px 40px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;border:1.3px solid rgba(255,255,255,0.3);transition:background 0.2s;cursor:pointer}
  .btn-ghost:hover{background:rgba(255,255,255,0.22)}
  .prod-card{transition:transform 0.35s cubic-bezier(.22,1,.36,1),box-shadow 0.35s}
  .prod-card:hover{transform:translateY(-8px);box-shadow:0 24px 60px rgba(0,0,0,0.14)!important}
  .filter-btn{transition:all 0.25s cubic-bezier(.22,1,.36,1)}
  .filter-btn.active{background:linear-gradient(135deg,#163d1f,#276530)!important;color:#fff!important;box-shadow:0 6px 20px rgba(39,101,48,0.4);transform:translateY(-2px)}
  .filter-btn:hover:not(.active){background:rgba(39,101,48,0.1)!important;color:#276530!important}
  .marquee-track{display:flex;animation:marquee 28s linear infinite}
  .marquee-track:hover{animation-play-state:paused}
  .badge-pill{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase}
  .featured-shine{position:relative;overflow:hidden}
  .featured-shine::after{content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent);animation:shimmer 3s linear infinite}
  
  .btn-red{display:inline-block;background:#dd3c30;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-red:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(101, 39, 39, 0.45)}
  img{max-width:100%}
  .nav-burger{display:none}
  @media (max-width:900px){
    .nav-links-desktop, .nav-call-desktop{display:none!important}
    .nav-burger{display:flex!important}
    .nav-inner{gap:16px!important}
  }
  @media (max-width:640px){
    .nav-secondary a{padding:0 12px;font-size:13px}
  }
  `;

/* ═══════════════════════════════════════════════════════════
   FILTER BAR — categories built dynamically from API data
═══════════════════════════════════════════════════════════ */
function FilterBar({
  active,
  setActive,
  searchQ,
  setSearchQ,
  products,
  loading,
}) {
  const ref = useRef(null);
  const vis = useInView(ref);

  // "All" + unique category names from live API data
  const categories = [
    "All",
    ...Array.from(
      new Set((products || []).map((p) => p.category?.name).filter(Boolean)),
    ),
  ];

  const matchCount = !products
    ? 0
    : active === "All"
      ? products.length
      : products.filter((p) => p.category?.name === active).length;

  return (
    <div
      ref={ref}
      style={{
        background: "#fff",
        position: "sticky",
        top: 56,
        zIndex: 800,
        borderBottom: "1px solid #f0f0f0",
        padding: "0 min(48px,4.8vw)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          maxWidth: 1312,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          gap: 35,
          height: 72,
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <svg
            style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.4,
            }}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#333"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search products…"
            style={{
              paddingLeft: 40,
              paddingRight: 16,
              height: 40,
              width: 350,
              border: "1.3px solid #e8e8e8",
              borderRadius: 10,
              fontSize: 14,
              background: "#fafafa",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#276530")}
            onBlur={(e) => (e.target.style.borderColor = "#e8e8e8")}
          />
        </div>

        {/* Dynamic category filter buttons */}
        <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
          {loading
            ? [1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  style={{
                    width: 80,
                    height: 36,
                    borderRadius: 30,
                    background: "#f0f0f0",
                    animation: "pulse 1.4s ease-in-out infinite",
                  }}
                />
              ))
            : categories.map((cat, i) => (
                <button
                  key={cat}
                  className={`filter-btn${active === cat ? " active" : ""}`}
                  onClick={() => setActive(cat)}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 30,
                    border: "1.3px solid #e8e8e8",
                    background: "#fff",
                    color: "#666",
                    fontSize: 13.5,
                    fontWeight: 500,
                    cursor: "pointer",
                    opacity: vis ? 1 : 0,
                    transform: vis ? "none" : "translateY(16px)",
                    transition: `opacity 0.5s ease ${i * 0.06}s, transform 0.5s ease ${i * 0.06}s, background 0.25s, color 0.25s, box-shadow 0.25s`,
                  }}
                >
                  {cat}
                </button>
              ))}
        </div>

        <div style={{ fontSize: 13, color: "#aaa", flexShrink: 0 }}>
          Showing <strong style={{ color: "#333" }}>{matchCount}</strong>{" "}
          product{matchCount !== 1 ? "s" : ""}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PRODUCT CARD — maps all fields from API response shape
═══════════════════════════════════════════════════════════ */
function ProductCard({ product, index }) {
  const ref = useRef(null);
  const vis = useInView(ref, 0.08);
  const [hovered, setHovered] = useState(false);

  /* ── map API fields ── */
  const name = product.name;
  const origin = product.details?.origin || "";
  const description =
    product.details?.description ||
    "Fresh produce sourced from certified farms.";
  const unit = product.details?.unit || "—";
  const tags = product.details?.tags || [];
  const category = product.category?.name || "—";
  const type = product.type; // "Fruit" | "Vegetable"
  const season = product.details?.season || "";
  const shelfLife = product.details?.shelf_life || "";
  const imageUrl = product.image?.url || null;
  const emoji = product.emoji || "bi-leaf";
  const mrp = product.pricing?.mrp || 0;
  const price = product.pricing?.price || 0;
  const discount = product.pricing?.discount || 0;
  const stock = product.stock ?? 0;
  const isOutOfStock = product.status === "Out of Stock" || stock === 0;

  /* ── badge: show discount % if applicable, else type ── */
  const badgeLabel = discount > 0 ? `${discount}% off` : type;
  const badgeColor =
    discount > 0 ? "#f59e0b" : type === "Fruit" ? "#276530" : "#0891b2";

  /* ── specs built from real data ── */
  const specs = [
    category && `Category: ${category}`,
    unit && `Pack: ${unit}`,
    season && `Season: ${season}`,
    shelfLife && `Shelf life: ${shelfLife}`,
    stock > 0 && `Stock: ${stock} units`,
  ].filter(Boolean);

  return (
    <div
      ref={ref}
      className="prod-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
        overflow: "hidden",
        cursor: "pointer",
        opacity: vis ? 1 : 0,
        transform: vis ? "none" : "translateY(60px) scale(0.94)",
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${index * 0.08}s, transform 0.7s cubic-bezier(.22,1,.36,1) ${index * 0.08}s`,
      }}
    >
      {/* ── Image / Emoji ── */}
      <div
        style={{
          position: "relative",
          height: 240,
          overflow: "hidden",
          background: imageUrl
            ? "#f5f5f5"
            : "linear-gradient(135deg,#f0fdf4,#dcfce7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: hovered ? "scale(1.07)" : "scale(1)",
              transition: "transform 0.6s cubic-bezier(.22,1,.36,1)",
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          /* Large emoji fallback with type-tinted background */
          <div
            style={{
              fontSize: 90,
              transform: hovered ? "scale(1.12)" : "scale(1)",
              transition: "transform 0.5s cubic-bezier(.22,1,.36,1)",
              userSelect: "none",
              filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.10))",
            }}
          >
            {emoji}
          </div>
        )}

        {/* Hover overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(10,26,6,0.85) 0%, transparent 50%)",
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
        />

        {/* Badge top-left */}
        <div
          className="badge-pill"
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            background: badgeColor,
            color: "#fff",
          }}
        >
          {badgeLabel}
        </div>

        {/* Origin top-right */}
        {origin && (
          <div
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(8px)",
              borderRadius: 20,
              padding: "4px 10px",
              fontSize: 11,
              color: "rgba(255,255,255,0.88)",
              fontWeight: 500,
            }}
          >
            <i className="bi bi-geo-alt-fill"></i> {origin}
          </div>
        )}

        {/* Tags revealed on hover */}
        {tags.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: isOutOfStock ? 34 : 14,
              left: 14,
              right: 14,
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              opacity: hovered ? 1 : 0,
              transform: hovered ? "translateY(0)" : "translateY(10px)",
              transition: "all 0.35s ease",
            }}
          >
            {tags.map((t) => (
              <span
                key={t}
                style={{
                  background: "rgba(221,60,48,0.25)",
                  border: "1px solid rgba(221,60,48,0.5)",
                  color: "#dd3c30",
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 20,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Card Body ── */}
      <div style={{ padding: "22px 24px 24px" }}>
        {/* Name + Category pill */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 10,
          }}
        >
          <h3
            className="serif"
            style={{
              fontSize: 19,
              fontWeight: 700,
              color: "#111",
              lineHeight: 1.25,
            }}
          >
            {name}
          </h3>
          <span
            style={{
              background: "rgba(39,101,48,0.08)",
              color: "#276530",
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 20,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              flexShrink: 0,
              marginLeft: 8,
            }}
          >
            {category}
          </span>
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: 13.5,
            color: "#666",
            lineHeight: 1.35,
            marginBottom: 18,
          }}
        >
          {description}
        </p>

        {/* Specs */}
        {specs.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginBottom: 20,
            }}
          >
            {specs.map((s) => (
              <div
                key={s}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#dd3c30",
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12.5, color: "#555" }}>{s}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <Link
            to="/contact-us"
            className="btn-green"
            style={{
              flex: 1,
              textAlign: "center",
              padding: "11px 16px",
              fontSize: 13.5,
              marginTop: 10,
              borderRadius: 10,
            }}
          >
            Get Quote
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PRODUCT GRID — fetches from API, filters locally
═══════════════════════════════════════════════════════════ */
function ProductGrid({ activeFilter, searchQ, onProductsLoaded }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}?status=Active&limit=100`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!data.success) throw new Error(data.message || "API error");
        const list = data.products || [];
        setProducts(list);
        if (onProductsLoaded) onProductsLoaded(list); // lift up for FilterBar
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Local filter: by category name + search query
  const filtered = products.filter((p) => {
    const matchCat =
      activeFilter === "All" || p.category?.name === activeFilter;
    const q = searchQ.toLowerCase();
    const matchSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.details?.description || "").toLowerCase().includes(q) ||
      (p.details?.tags || []).some((t) => t.toLowerCase().includes(q)) ||
      (p.category?.name || "").toLowerCase().includes(q) ||
      (p.details?.origin || "").toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  return (
    <div
      id="product-grid"
      style={{ background: "#f9f9f9", padding: "min(60px,6vw) min(48px,4.8vw) min(80px,8vw)" }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        {/* Error */}
        {error && (
          <div style={{ textAlign: "center", padding: "min(80px,8vw) 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#555" }}>
              Failed to load products
            </div>
            <div style={{ fontSize: 13, color: "#aaa", marginTop: 8 }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 20,
                padding: "10px 28px",
                background: "#276530",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Skeleton */}
        {loading && !error && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
              gap: 15,
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div
                key={n}
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  overflow: "hidden",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    height: 240,
                    background:
                      "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmerSkeleton 1.5s ease-in-out infinite",
                  }}
                />
                <div style={{ padding: "22px 24px" }}>
                  {[100, 70, 85, 50].map((w, i) => (
                    <div
                      key={i}
                      style={{
                        height: i === 0 ? 20 : 13,
                        width: `${w}%`,
                        background: "#f0f0f0",
                        borderRadius: 6,
                        marginBottom: 12,
                        animation: "shimmerSkeleton 1.5s ease-in-out infinite",
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div
            style={{ textAlign: "center", padding: "min(80px,8vw) 0", color: "#aaa" }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              <i className="bi bi-search"></i>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#555" }}>
              No products found
            </div>
            <div style={{ fontSize: 14, marginTop: 8 }}>
              Try adjusting your search or filter
            </div>
          </div>
        )}

        {/* Cards */}
        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
              gap: 15,
            }}
          >
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ALL SECTIONS BELOW — UNCHANGED FROM ORIGINAL
═══════════════════════════════════════════════════════════ */

function ProductsHero({ scrollY }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);
  const parallax = scrollY * 0.38;

  return (
    <section
      style={{
        position: "relative",
        height: "92vh",
        minHeight: 600,
        overflow: "hidden",
        background: "#0a1a06",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url("/assets/images/latest/7.png")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${parallax}px)`,
          willChange: "transform",
          filter: "brightness(0.5) saturate(1.2)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(160deg,rgba(10,26,6,0.7) 0%,rgba(39,101,48,0.18) 50%,rgba(10,26,6,0.85) 100%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "22%",
          right: "8%",
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(221,60,48,0.12) 0%,transparent 70%)",
          animation: "floatY 6s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          maxWidth: 1312,
          margin: "0 auto",
          padding: "0 min(48px,4.8vw)",
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ maxWidth: 680 }}>
          <div
            className="sl"
            style={{
              opacity: ready ? 1 : 0,
              transform: ready ? "none" : "translateY(20px)",
              transition: "all 0.7s cubic-bezier(.22,1,.36,1) 0.1s",
            }}
          >
            Our Product Range
          </div>
          <h1
            className="serif"
            style={{
              fontSize: "clamp(44px, 6vw, 55px)",
              fontWeight: 600,
              lineHeight: 1.08,
              color: "#fff",
              marginBottom: 28,
              letterSpacing: "-1px",
              opacity: ready ? 1 : 0,
              transform: ready ? "none" : "translateY(36px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.25s",
            }}
          >
            Premium Fresh <br />
            <span className="shine-text">Fruits & Vegetables</span>
          </h1>
          <p
            style={{
              fontSize: 18,
              color: "rgba(255,255,255,0.72)",
              lineHeight: 1.3,
              marginBottom: 44,
              maxWidth: 520,
              opacity: ready ? 1 : 0,
              transform: ready ? "none" : "translateY(24px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.4s",
            }}
          >
            Sourced from certified farms across 3 continents. Trusted by 200+
            HORECA partners across the UAE since 2002.
          </p>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              opacity: ready ? 1 : 0,
              transition: "opacity 0.8s ease 0.6s",
            }}
          >
            <button
              className="btn-green"
              onClick={() =>
                document
                  .getElementById("product-grid")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Explore Products
            </button>
            <Link to="/contact-us" className="btn-ghost">
              Request Quote
            </Link>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            right: 80,
            bottom: 100,
            display: "flex",
            gap: 14,
            opacity: ready ? 1 : 0,
            transform: ready ? "none" : "translateY(30px)",
            transition: "all 1s cubic-bezier(.22,1,.36,1) 0.75s",
          }}
        >
          {[
            { n: "200+", l: "HORECA Partners" },
            { n: "50+", l: "Product SKUs" },
            { n: "12+", l: "Countries Sourced" },
          ].map(({ n, l }) => (
            <div
              key={l}
              style={{
                background: "rgba(255,255,255,0.09)",
                backdropFilter: "blur(18px)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 16,
                padding: "20px 24px",
                textAlign: "center",
                minWidth: 120,
              }}
            >
              <div
                className="serif"
                style={{ fontSize: 28, fontWeight: 700, color: "#dd3c30" }}
              >
                {n}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.55)",
                  marginTop: 4,
                }}
              >
                {l}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 36,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          opacity: ready ? 0.6 : 0,
          transition: "opacity 0.8s ease 1s",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 3,
            color: "#dd3c30",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          Scroll
        </div>
        <div
          style={{
            width: 24,
            height: 38,
            border: "1.3px solid rgba(221,60,48,0.5)",
            borderRadius: 12,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: 4,
              height: 8,
              background: "#dd3c30",
              borderRadius: 2,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              animation: "scrollDot 1.8s ease-in-out infinite",
            }}
          />
        </div>
      </div>
      <style>{`@keyframes scrollDot{0%{top:4px;opacity:1}80%{top:22px;opacity:0}100%{top:4px;opacity:0}}`}</style>
    </section>
  );
}

function FeaturedBrandsSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const brands = [
    {
      name: "Dole",
      desc: "World's leading pineapple & banana brand",
      img: IMG.pineappleOla2,
    },
    {
      name: "OLA Fresh",
      desc: "Premium Filipino produce, farm-direct",
      img: IMG.pineappleDole,
    },
    {
      name: "Terioka",
      desc: "Natural banana specialist, Philippines",
      img: IMG.pineappleOla,
    },
  ];

  return (
    <section style={{ background: "#0d0d0d", padding: "min(80px,8vw) min(48px,4.8vw)" }}>
      <div ref={ref} style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div className="sl">Exclusive Partnerships</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(32px, 4vw, 50px)",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.15,
              marginBottom: 16,
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(30px)",
              transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
            }}
          >
            Brands You Can <span style={{ color: "#dd3c30" }}>Trust</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.55)",
              maxWidth: 520,
              margin: "0 auto",
              opacity: vis ? 1 : 0,
              transition: "opacity 0.8s ease 0.2s",
            }}
          >
            Authorised distributor for globally recognised fruit brands in the
            UAE HORECA sector.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: 28,
          }}
        >
          {brands.map(({ name, desc, img }, i) => (
            <div
              key={name}
              className="featured-shine"
              style={{
                borderRadius: 20,
                overflow: "hidden",
                position: "relative",
                height: 320,
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(50px)",
                transition: `all 0.8s cubic-bezier(.22,1,.36,1) ${i * 0.15}s`,
              }}
            >
              <img
                src={img}
                alt={name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "brightness(0.8)",
                }}
                onError={(e) => (e.target.style.display = "none")}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(10,26,6,0.95) 0%, rgba(10,26,6,0.4) 50%, transparent 100%)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: "28px 28px",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                    background: "rgba(221,60,48,0.18)",
                    border: "1px solid rgba(221,60,48,0.35)",
                    borderRadius: 20,
                    padding: "4px 12px",
                    fontSize: 10,
                    color: "#dd3c30",
                    fontWeight: 700,
                    letterSpacing: 1.3,
                    textTransform: "uppercase",
                  }}
                >
                  <i className="bi bi-check-lg"></i> Authorised Partner
                </div>
                <h3
                  className="serif"
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: 8,
                  }}
                >
                  {name}
                </h3>
                <p
                  style={{
                    fontSize: 13.5,
                    color: "rgba(255,255,255,0.6)",
                    lineHeight: 1.3,
                  }}
                >
                  {desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QualitySection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const pillars = [
    {
      icon: "bi-lightning",
      title: "Farm Certified",
      body: "All produce sourced from GlobalG.A.P. and ISO-certified farms with full traceability.",
    },
    {
      icon: "bi-snow",
      title: "Cold Chain Intact",
      body: "Unbroken cold-chain logistics from farm gate to your kitchen, maintaining freshness.",
    },
    {
      icon: "bi-eyedropper",
      title: "Quality Tested",
      body: "Every shipment undergoes residue and microbiological testing before distribution.",
    },
    {
      icon: "bi-recycle",
      title: "Eco Packaging",
      body: "Sustainable, food-safe packaging materials aligned with UAE Green Economy goals.",
    },
    {
      icon: "bi-truck",
      title: "On-Time Delivery",
      body: "98.7% on-time delivery record across our Dubai HORECA client base.",
    },
    {
      icon: "bi-clipboard-check",
      title: "Full Compliance",
      body: "HACCP, UAE ESMA, and MOCCAE compliant supply chain with audit-ready records.",
    },
  ];

  return (
    <section style={{ background: "#fff", padding: "min(50px,5vw) min(48px,4.8vw) min(80px,8vw)" }}>
      <div ref={ref} style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 80,
            alignItems: "center",
          }}
        >
          <div>
            <div className="sl-dark">Our Quality Promise</div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(30px, 3.5vw, 48px)",
                fontWeight: 700,
                color: "#111",
                lineHeight: 1.18,
                marginBottom: 24,
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(30px)",
                transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
              }}
            >
              Every Product Passes
              <br />
              Our 6-Point Check
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "#666",
                lineHeight: 1.3,
                marginBottom: 40,
                opacity: vis ? 1 : 0,
                transition: "opacity 0.8s ease 0.2s",
              }}
            >
              Two decades of operating in Dubai's demanding HORECA market has
              taught us that quality is not a feature — it's the only acceptable
              standard.
            </p>
            <Link to="/contact-us" className="btn-green">
              Partner With Us
            </Link>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8 }}
          >
            {pillars.map(({ icon, title, body }, i) => (
              <div
                key={title}
                style={{
                  background: "#f8fef5",
                  borderRadius: 16,
                  padding: "22px 20px",
                  border: "1px solid rgba(39,101,48,0.12)",
                  opacity: vis ? 1 : 0,
                  transform: vis ? "none" : "translateY(40px) scale(0.96)",
                  transition: `all 0.7s cubic-bezier(.22,1,.36,1) ${0.1 + i * 0.08}s`,
                }}
              >
                <i style={{ fontSize: 28 }} className={`bi ${icon}`}></i>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    marginTop: 15,
                    marginBottom: 6,
                  }}
                >
                  {title}
                </div>
                <div style={{ fontSize: 12.5, color: "#777", lineHeight: 1.3 }}>
                  {body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OriginsSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const origins = [
    {
      flag: "bi-flag",
      country: "Philippines",
      products: "Pineapples, Bananas, Mangoes",
      volume: "40%",
    },
    {
      flag: "bi-flag",
      country: "India",
      products: "Alphonso Mangoes, Grapes, Pomegranates",
      volume: "22%",
    },
    {
      flag: "bi-flag",
      country: "Peru",
      products: "Avocados, Grapes, Asparagus",
      volume: "15%",
    },
    {
      flag: "bi-flag",
      country: "Ecuador",
      products: "Dole Bananas, Passion Fruit",
      volume: "13%",
    },
    {
      flag: "bi-flag",
      country: "Vietnam",
      products: "Dragon Fruit, Rambutan, Lychee",
      volume: "7%",
    },
    {
      flag: "bi-globe-europe-africa",
      country: "Others",
      products: "Seasonal & Speciality Produce",
      volume: "3%",
    },
  ];

  return (
    <section style={{ background: "#f4f9f2", padding: "min(80px,8vw) min(48px,4.8vw)" }}>
      <div ref={ref} style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div className="sl-dark">Global Sourcing</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(30px, 3.5vw, 48px)",
              fontWeight: 700,
              color: "#111",
              lineHeight: 1.18,
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(28px)",
              transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
            }}
          >
            Sourced from <span style={{ color: "#276530" }}>5 Continents</span>
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {origins.map(({ flag, country, products, volume }, i) => (
            <div
              key={country}
              style={{
                background: "#fff",
                borderRadius: 18,
                padding: "28px",
                border: "1px solid rgba(39,101,48,0.1)",
                position: "relative",
                overflow: "hidden",
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(40px)",
                transition: `all 0.7s cubic-bezier(.22,1,.36,1) ${i * 0.1}s`,
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 14 }}>
                <i className={`bi ${flag}`}></i>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 10,
                }}
              >
                <div
                  className="serif"
                  style={{ fontSize: 20, fontWeight: 700, color: "#111" }}
                >
                  {country}
                </div>
                <div
                  style={{
                    background: "rgba(39,101,48,0.1)",
                    color: "#276530",
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {volume}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#777", lineHeight: 1.35 }}>
                {products}
              </div>
              <div
                style={{
                  marginTop: 18,
                  height: 3,
                  background: "#f0f0f0",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "linear-gradient(90deg,#276530,#dd3c30)",
                    width: vis ? volume : "0%",
                    transition: `width 1.2s cubic-bezier(.22,1,.36,1) ${0.3 + i * 0.1}s`,
                    borderRadius: 2,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowToOrderSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const steps = [
    {
      n: "01",
      icon: "bi-clipboard-check",
      title: "Browse & Select",
      body: "Explore our product catalogue and shortlist the produce you need for your establishment.",
    },
    {
      n: "02",
      icon: "bi-telephone",
      title: "Contact Our Team",
      body: "Reach our sales team via phone, WhatsApp or email. We respond within 2 hours.",
    },
    {
      n: "03",
      icon: "bi-pencil-square",
      title: "Receive Quote",
      body: "We send you a detailed price quote with MOQ, delivery lead times, and payment terms.",
    },
    {
      n: "04",
      icon: "bi-truck-flatbed",
      title: "Delivery to Your Door",
      body: "Confirmed orders are delivered fresh in our refrigerated fleet. Same-day for Dubai.",
    },
  ];

  return (
    <section style={{ background: "#0d0d0d", padding: "min(100px,10vw) min(48px,4.8vw)" }}>
      <div ref={ref} style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <div className="sl">Simple Process</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(30px, 3.5vw, 48px)",
              fontWeight: 700,
              color: "#fff",
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(28px)",
              transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
            }}
          >
            How to Order <span style={{ color: "#dd3c30" }}>From Us</span>
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4, 1fr)",
            gap: 24,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 35,
              left: "12.5%",
              right: "12.5%",
              height: 2,
              background: "rgba(221,60,48,0.2)",
              zIndex: 0,
            }}
          />
          {steps.map(({ n, icon, title, body }, i) => (
            <div
              key={n}
              style={{
                textAlign: "center",
                position: "relative",
                zIndex: 1,
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(40px)",
                transition: `all 0.7s cubic-bezier(.22,1,.36,1) ${i * 0.15}s`,
              }}
            >
              <i
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  margin: "0 auto 24px",
                  background: "linear-gradient(135deg,#163d1f,#276530)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                  color: "#fff",
                  boxShadow: "0 8px 28px rgba(39,101,48,0.45)",
                  border: "3px solid rgba(221,60,48,0.3)",
                }}
                className={`bi ${icon}`}
              >
                
              </i>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 3,
                  color: "#dd3c30",
                  fontWeight: 700,
                  marginBottom: 10,
                }}
              >
                STEP {n}
              </div>
              <h3
                className="serif"
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 12,
                }}
              >
                {title}
              </h3>
              <p
                style={{
                  fontSize: 13.5,
                  color: "rgba(255,255,255,0.5)",
                  lineHeight: 1.35,
                }}
              >
                {body}
              </p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 64 }}>
          <Link
            to="/contact-us"
            className="btn-green"
            style={{ marginRight: 16 }}
          >
            Start Your Order
          </Link>
          <a href="tel:+971565378650" className="btn-ghost">
            <i className="bi bi-telephone"></i> Call Now
          </a>
        </div>
      </div>
    </section>
  );
}

function ProductsCTA() {
  const ref = useRef(null);
  const vis = useInView(ref);
  return (
    <section ref={ref} style={{ background: "#fff", padding: "min(80px,8vw) min(48px,4.8vw)" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <div
          style={{
            background:
              "linear-gradient(135deg,#0d0d0d 0%,#163d1f 50%,#0d0d0d 100%)",
            borderRadius: 28,
            padding: "min(72px,7.2vw) min(64px,6.4vw)",
            position: "relative",
            overflow: "hidden",
            opacity: vis ? 1 : 0,
            transform: vis ? "none" : "translateY(40px) scale(0.97)",
            transition: "all 0.9s cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background:
                "radial-gradient(circle,rgba(221,60,48,0.15) 0%,transparent 70%)",
            }}
          />
          <div className="sl" style={{ marginBottom: 20 }}>
            Ready to Partner?
          </div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(28px, 3.5vw, 46px)",
              fontWeight: 700,
              color: "#fff",
              marginBottom: 20,
              lineHeight: 1.2,
            }}
          >
            Join 200+ UAE Businesses <br />
            <span style={{ color: "#dd3c30" }}>Powered by Fruits Garden</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.6)",
              marginBottom: 44,
              maxWidth: 520,
              margin: "0 auto min(44px,4.4vw)",
            }}
          >
            Get personalised pricing, dedicated account management, and
            guaranteed freshness on every order.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <Link to="/contact-us" className="btn-green">
              Get a Free Quote
            </Link>
            <a href="mailto:sales@fruitsgarden.com" className="btn-ghost">
              <i className="bi bi-envelope"></i> Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BackTop({ scrollY }) {
  if (scrollY < 600) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed",
        bottom: 32,
        right: 32,
        zIndex: 999,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "#dd3c30",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        fontSize: 18,
        boxShadow: "0 4px 18px rgba(39,101,48,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(39,101,48,0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(39,101,48,0.45)";
      }}
    >
      ↑
    </button>
  );
}

function FreshnessTimelineSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.07);
  const steps = [
    {
      icon: "bi-flower1",
      title: "Farm Harvest",
      time: "Day 0",
      desc: "Carefully harvested at peak ripeness by our certified farm partners across 15+ countries.",
      color: "#276530",
    },
    {
      icon: "bi-eyedropper",
      title: "Quality Inspection",
      time: "Day 0–1",
      desc: "Every batch is checked against Grade A standards — size, colour, Brix levels, and zero chemical residue.",
      color: "#3b82f6",
    },
    {
      icon: "bi-snow",
      title: "Cold Chain Entry",
      time: "Day 1",
      desc: "Temperature locked between 2–8°C. Refrigerated containers maintain freshness through international transit.",
      color: "#0891b2",
    },
    {
      icon: "bi-airplane",
      title: "Express Shipment",
      time: "Day 1–4",
      desc: "Air and sea freight routes optimised for shortest transit times. Real-time tracking throughout.",
      color: "#7c3aed",
    },
    {
      icon: "bi-building",
      title: "Dubai Warehouse",
      time: "Day 4–5",
      desc: "Received at our Dubai Food District facility. Secondary quality check and sorting by grade.",
      color: "#d97706",
    },
    {
      icon: "bi-truck",
      title: "Your Kitchen",
      time: "Day 5–6",
      desc: "Same-day or next-day delivery to your location across all UAE Emirates.",
      color: "#059669",
    },
  ];
  return (
    <section
      ref={ref}
      id="freshness-promise"
      style={{
        background: "#fff",
        padding: "min(70px,7vw) min(48px,4.8vw) min(50px,5vw)",
        overflow: "hidden",
      }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 72,
            opacity: vis ? 1 : 0,
            transform: vis ? "none" : "translateY(28px)",
            transition: "all 0.8s cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div className="sl-dark">Zero Compromise</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(30px,4vw,50px)",
              fontWeight: 800,
              color: "#1a1a1a",
              letterSpacing: -2,
              lineHeight: 1.05,
            }}
          >
            From Farm to Fork in{" "}
            <span style={{ color: "#276530" }}>6 Days</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "#666",
              maxWidth: 520,
              margin: "16px auto 0",
              lineHeight: 1.5,
            }}
          >
            Our end-to-end cold chain guarantees you receive produce at maximum
            freshness, every order.
          </p>
        </div>
        <div style={{ position: "relative" }}>
          {/* Connecting line */}
          <div
            style={{
              position: "absolute",
              top: 44,
              left: "6.5%",
              right: "6.5%",
              height: 2,
              background:
                "linear-gradient(90deg,#276530,#dd3c30,#3b82f6,#0891b2,#7c3aed,#059669)",
              opacity: vis ? 0.35 : 0,
              transition: "opacity 1s ease 0.5s",
            }}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(6,1fr)",
              gap: 16,
            }}
          >
            {steps.map((s, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  opacity: vis ? 1 : 0,
                  transform: vis ? "none" : "translateY(40px)",
                  transition: `all 0.65s cubic-bezier(.22,1,.36,1) ${0.1 + i * 0.12}s`,
                }}
              >
                <div
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: "50%",
                    margin: "0 auto 20px",
                    background: `#fff`,
                    backgroundImage: `linear-gradient(135deg,${s.color}30,${s.color}15)`,
                    border: `2px solid ${s.color}40`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 28,
                    position: "relative",
                    zIndex: 1,
                    boxShadow: `0 4px 20px ${s.color}20`,
                    transition: "transform 0.3s, box-shadow 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.12)";
                    e.currentTarget.style.boxShadow = `0 8px 32px ${s.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = `0 4px 20px ${s.color}20`;
                  }}
                >
                  <i className={`bi ${s.icon}`}></i>
                  <div
                    style={{
                      position: "absolute",
                      bottom: -4,
                      right: -4,
                      background: s.color,
                      color: "#fff",
                      fontSize: 9,
                      fontWeight: 800,
                      padding: "2px 6px",
                      borderRadius: 10,
                      letterSpacing: 0.5,
                    }}
                  >
                    {s.time}
                  </div>
                </div>
                <h3
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#1a1a1a",
                    marginBottom: 8,
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ fontSize: 12, color: "#777", lineHeight: 1.3 }}>
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
function Products() {
  const scrollY = useScrollY();
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQ, setSearchQ] = useState("");
  const [liveProducts, setLiveProducts] = useState([]); // lifted from ProductGrid

  useEffect(() => {
    document.title = "Products — Fruits Garden Trading LLC";
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div>
      <Navbar scrollY={scrollY} />
      <ProductsHero scrollY={scrollY} />

      <FilterBar
        active={activeFilter}
        setActive={setActiveFilter}
        searchQ={searchQ}
        setSearchQ={setSearchQ}
        products={liveProducts}
        loading={liveProducts.length === 0}
      />

      <ProductGrid
        activeFilter={activeFilter}
        searchQ={searchQ}
        onProductsLoaded={setLiveProducts}
      />

      <FeaturedBrandsSection />
      <FreshnessTimelineSection />
      <QualitySection />
      <OriginsSection />
      <HowToOrderSection />
      <ProductsCTA />
      <Footer />
      <BackTop scrollY={scrollY} />
    </div>
  );
}

export default Products;
